import { useState } from "react";
import { useForm } from "react-hook-form";

export default function ContactCta() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  type FormDataType = {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    service: string;
    message: string;
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormDataType>();

  const onSubmit = async (data: FormDataType) => {
    setLoading(true);
    setError(null);

    let text = "";
    for (const [key, value] of Object.entries(data)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: data.email,
      text,
      problemType: data.service,
      to_email: "support@regent-digital.com",
      subject: " "
    };

    try {
      const res = await fetch(
        "https://rocknlabs.com/api/support/request_form",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) {
        throw new Error("Server error");
      }

      setSubmitted(true);
      reset();
      setCharCount(0);
    } catch (err) {
      setError("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="bg-[#1A1A1A] py-24 md:py-36 px-8 md:px-16 overflow-hidden"
    >
      <style>{`
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0px 1000px white inset;
      }
    `}</style>
      <div className="w-full">
        {/* Header */}
        <div className="text-center mb-16">
          <span
            className="text-white/10 font-black leading-none block"
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(80px, 12vw, 160px)"
            }}
          >
            05
          </span>
          <h2
            className="text-white font-black uppercase leading-none -mt-8 md:-mt-16"
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(40px, 8vw, 120px)"
            }}
          >
            LET'S
            <br />
            <span className="text-[#FFD93D]">TALK</span>
          </h2>
          <p className="text-white/60 font-bold text-xl md:text-2xl uppercase tracking-widest mt-6">
            Tell us about your project — we reply within 24 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
          {/* Left: Info */}
          <div className="space-y-10">
            <div className="bg-[#FFD93D] rounded-[30px] p-10">
              <h3
                className="text-black font-black uppercase text-3xl md:text-4xl mb-6"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                HOW IT WORKS
              </h3>
              <div className="space-y-6">
                {[
                  {
                    step: "01",
                    text: "Fill out the form with your project details"
                  },
                  { step: "02", text: "Our team reaches out within 24 hours" },
                  { step: "03", text: "We arrange a free discovery call" },
                  { step: "04", text: "You receive a tailored proposal" }
                ].map(s => (
                  <div key={s.step} className="flex gap-4 items-start">
                    <span
                      className="text-black/30 font-black text-3xl leading-none flex-shrink-0"
                      style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                    >
                      {s.step}
                    </span>
                    <p className="text-black font-bold text-lg md:text-xl leading-snug pt-1">
                      {s.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact info card */}
            <div className="bg-white/5 border-2 border-white/10 rounded-[30px] p-10">
              <h3
                className="text-white font-black uppercase text-3xl md:text-4xl mb-6"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                DIRECT CONTACT
              </h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FFD93D] rounded-xl flex-shrink-0">
                    <i className="ri-map-pin-line text-black text-xl" />
                  </div>
                  <div>
                    <p className="text-white/40 text-sm font-semibold uppercase tracking-widest mb-1">
                      Address
                    </p>
                    <p className="text-white font-semibold text-base leading-relaxed">
                      International House, 55 Longsmith Street
                      <br />
                      Gloucester, GL1 2HT, United Kingdom
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FFD93D] rounded-xl flex-shrink-0">
                    <i className="ri-smartphone-line text-black text-xl" />
                  </div>
                  <div>
                    <p className="text-white/40 text-sm font-semibold uppercase tracking-widest mb-1">
                      Mobile
                    </p>
                    <a
                      href="tel:+447520657571"
                      className="text-white font-semibold text-base hover:text-[#FFD93D] transition-colors cursor-pointer"
                    >
                      +44 7520 657571
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FFD93D] rounded-xl flex-shrink-0">
                    <i className="ri-mail-line text-black text-xl" />
                  </div>
                  <div>
                    <p className="text-white/40 text-sm font-semibold uppercase tracking-widest mb-1">
                      Email
                    </p>
                    <a
                      href="mailto:support@regent-digital.com"
                      className="text-white font-semibold text-base hover:text-[#FFD93D] transition-colors cursor-pointer"
                    >
                      support@regent-digital.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-[30px] p-10 md:p-12 flex flex-col">
            {error ? (
              <div className="justify-items-center text-center py-12">
                <div className="flex items-center justify-center  rounded-full mx-auto mb-5">
                  <img
                    className="w-20"
                    src="/error.svg"
                  />
                </div>
                <h3 className="text-black font-black uppercase text-4xl md:text-5xl mb-4"
                  style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Oops!</h3>
                <p className="text-black/60 font-bold text-xl">
                  Something went wrong while sending your message. <br />
                  Please try again later.
                </p>
                <button
                  onClick={() => {
                    setError(null);
                    reset();
                    setCharCount(0);
                  }}
                  className="w-[100%] max-w-[250px] font-[Anton] text-[16px] px-[110px] py-[13px] mt-6 flex items-center justify-center gap-2 text-white font-semibold rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap text-sm"
                  style={{ backgroundColor: "#FF4103" }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.backgroundColor = "#b5180d")
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.backgroundColor = "#FF4103")
                  }
                >
                  Try Again
                </button>
              </div>
            ) : submitted ? (
              <div className="text-center flex flex-col items-center py-16">
                <div className="w-20 h-20 flex items-center justify-center bg-[#C4F547] rounded-full mx-auto mb-6">
                  <i className="ri-check-line text-black text-4xl" />
                </div>
                <h3
                  className="text-black font-black uppercase text-4xl md:text-5xl mb-4"
                  style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                >
                  MESSAGE SENT!
                </h3>
                <p className="text-black/60 font-bold text-xl">
                  We&apos;ll get back to you within 24 hours. Talk soon!
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    reset();
                    setCharCount(0);
                  }}
                  className="w-[100%] max-w-[250px] font-[Anton] text-[16px] mt-6 flex items-center justify-center gap-2 text-black px-[69px] py-[13px] rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap text-sm"
                  style={{ backgroundColor: "#B7F700" }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.backgroundColor = "#c2e010")
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.backgroundColor = "#B7F700")
                  }
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-6 flex flex-col flex-1"
              >
                <h3
                  className="text-black font-black uppercase text-3xl md:text-4xl mb-8"
                  style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                >
                  SEND A MESSAGE
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-black font-black uppercase text-sm tracking-widest mb-2"
                      style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                    >
                      FIRST NAME *
                    </label>
                    <input
                      {...register("first_name", {
                        required: "First name is required",
                        minLength: { value: 2, message: "Min 2 characters" }
                      })}
                      type="text"
                      className="w-full border-2 border-black/20 rounded-xl px-4 py-3 text-black font-semibold text-sm focus:outline-none focus:border-[#FFD93D] transition-colors"
                      placeholder="John"
                    />
                    {errors.first_name?.message && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.first_name.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      className="block text-black font-black uppercase text-sm tracking-widest mb-2"
                      style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                    >
                      LAST NAME *
                    </label>
                    <input
                      {...register("last_name", {
                        required: "Last name is required",
                        minLength: { value: 2, message: "Min 2 characters" }
                      })}
                      type="text"
                      className="w-full border-2 border-black/20 rounded-xl px-4 py-3 text-black font-semibold text-sm focus:outline-none focus:border-[#FFD93D] transition-colors"
                      placeholder="Smith"
                    />
                    {errors.last_name?.message && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.last_name.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    className="block text-black font-black uppercase text-sm tracking-widest mb-2"
                    style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                  >
                    EMAIL *
                  </label>
                  <input
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email address"
                      }
                    })}
                    type="email"
                    className="w-full border-2 border-black/20 rounded-xl px-4 py-3 text-black font-semibold text-sm focus:outline-none focus:border-[#FFD93D] transition-colors"
                    placeholder="john@company.com"
                  />
                  {errors.email?.message && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    className="block text-black font-black uppercase text-sm tracking-widest mb-2"
                    style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                  >
                    SERVICE NEEDED *
                  </label>
                  <select
                    {...register("service", {
                      required: "Please select a service"
                    })}
                    className="w-full border-2 border-black/20 rounded-xl px-4 py-3 text-black font-semibold text-sm focus:outline-none focus:border-[#FFD93D] transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select a service</option>
                    <option value="Custom Software Development">
                      Custom Software Development
                    </option>
                    <option value="IT Consulting">IT Consulting</option>
                    <option value="Digital Marketing">Digital Marketing</option>
                    <option value="UI/UX Design">UI/UX Design</option>
                    <option value="QA & Testing">QA &amp; Testing</option>
                    <option value="Full Digital Solution">
                      Full Digital Solution
                    </option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.service?.message && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.service.message}
                    </p>
                  )}
                </div>

                <div className="flex-1 flex flex-col">
                  <label
                    className="block text-black font-black uppercase text-sm tracking-widest mb-2"
                    style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                  >
                    MESSAGE ({charCount}/500)
                  </label>
                  <textarea
                    {...register("message", {
                      required: "Message is required",
                      minLength: { value: 20, message: "Min 20 characters" },
                      maxLength: { value: 500, message: "Max 500 characters" }
                    })}
                    onChange={e => setCharCount(e.target.value.length)}
                    rows={4}
                    placeholder="Tell us about your project..."
                    className="w-full flex-1 border-2 border-black/20 rounded-xl px-4 py-3 text-black font-semibold text-sm focus:outline-none focus:border-[#FFD93D] transition-colors resize-none"
                  />
                  {errors.message?.message && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.message.message}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FFD93D] text-black font-black uppercase py-5 rounded-xl text-2xl hover:bg-[#FF8C42] hover:text-white transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                >
                  {loading ? "Sending..." : "Send message"}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
